
    import Component from "vue-class-component";
    import {Prop, Watch} from "vue-property-decorator";
    import {
        ApplicationActions,
        EditDocumentSeedRequest,
        EditMaritimePilotCertificateSeedRequest,
        ResultError,
        AreasClient
    } from "@/api-client";
    import IssueDocumentInnerComponent from "@/features/applications/components/issue-document/IssueDocumentInnerComponent";
    import IssueDocumentSignature from "@/features/applications/components/issue-document/IssueDocumentSignature.vue";
    import DatePicker from "@/components/DatePicker.vue";
    import LabelOptional from "@/features/shared/components/LabelOptional.vue";
    import AdditionalInfoButton from "@/features/shared/components/AdditionalInfoButton.vue";
    import Multiselect from "vue-multiselect";

    @Component({
        components: {
            AdditionalInfoButton,
            DatePicker,
            IssueDocumentSignature,
            LabelOptional,
            Multiselect
        }
    })
    export default class IssueMaritimePilotCertificate extends IssueDocumentInnerComponent {
        @Prop({required: true})
        issueDocumentDetails!: any; // Will be properly typed once backend is ready
        @Prop({required: true})
        actions!: ApplicationActions;
        @Prop({required: true})
        errors!: ResultError;

        initialEditModel!: EditDocumentSeedRequest;
        areasClient = new AreasClient();

        validUntil = this.issueDocumentDetails.validUntil;
        issueDate = this.issueDocumentDetails.issueDate;
        additionalInfo = this.issueDocumentDetails.additionalInfo;
        shipGrossTonnage: number | null = this.issueDocumentDetails.shipGrossTonnage || null;
        tankerGrossTonnage: number | null = this.issueDocumentDetails.tankerGrossTonnage || null;
        selectedPilotageAreas: any[] = [];
        pilotageAreaOptions: any[] = [];

        shipGTError: string | null = null;
        tankerGTError: string | null = null;

        private readonly MAX_SHIP_GT = 100000;
        private readonly MAX_TANKER_GT = 50000;

        async mounted() {
            await this.loadPilotageAreas();
            // Initialize selected areas from existing data after options are loaded
            if (this.issueDocumentDetails.pilotageAreaIds && this.issueDocumentDetails.pilotageAreaIds.length > 0) {
                this.selectedPilotageAreas = this.pilotageAreaOptions.filter(option =>
                    this.issueDocumentDetails.pilotageAreaIds.includes(option.id)
                );
            }
            this.initialEditModel = this.getEditModel();
        }

        async loadPilotageAreas() {
            const areasClient = new AreasClient();
            const response = await areasClient.areaOptions();

            // Map the areas to the format expected by the multiselect
            this.pilotageAreaOptions = response.areas
                .map(area => ({
                    id: area.value,
                    name: area.text
                }));
        }

        @Watch('shipGrossTonnage')
        onShipGTChanged() {
            this.validateShipGT();
        }

        @Watch('tankerGrossTonnage')
        onTankerGTChanged() {
            this.validateTankerGT();
        }

        validateShipGT(): boolean | null {
            if (!this.shipGrossTonnage) {
                this.shipGTError = null;
                return null;
            }

            if (this.shipGrossTonnage <= 0) {
                this.shipGTError = this.$ls.MaritimePilotCertificate_Validation_ShipGTMustBePositive || 'Ship GT must be positive';
                return false;
            }

            if (this.shipGrossTonnage > this.MAX_SHIP_GT) {
                this.shipGTError = (this.$ls.MaritimePilotCertificate_Validation_ShipGTExceedsLimit || 'Ship GT exceeds limit').replace('{0}', this.MAX_SHIP_GT.toString());
                return false;
            }

            this.shipGTError = null;
            return true;
        }

        validateTankerGT(): boolean | null {
            if (!this.tankerGrossTonnage) {
                this.tankerGTError = null;
                return null;
            }

            if (this.tankerGrossTonnage <= 0) {
                this.tankerGTError = this.$ls.MaritimePilotCertificate_Validation_TankerGTMustBePositive || 'Tanker GT must be positive';
                return false;
            }

            if (this.tankerGrossTonnage > this.MAX_TANKER_GT) {
                this.tankerGTError = (this.$ls.MaritimePilotCertificate_Validation_TankerGTExceedsLimit || 'Tanker GT exceeds limit').replace('{0}', this.MAX_TANKER_GT.toString());
                return false;
            }

            this.tankerGTError = null;
            return true;
        }

        getEditModel(): EditDocumentSeedRequest {
            const request = new EditMaritimePilotCertificateSeedRequest();
            request.additionalInfo = this.additionalInfo;
            request.issueDate = this.issueDate;
            request.validUntil = this.validUntil;
            request.shipGrossTonnage = this.shipGrossTonnage;
            request.tankerGrossTonnage = this.tankerGrossTonnage;
            // Extract just the IDs from the selected areas
            request.pilotageAreaIds = this.selectedPilotageAreas.map(area => area.id);
            return request;
        }
    }
